import * as React from 'react';

import type {FilterObject} from 'nutshell-core/types';

import {EditIcon, PlusIcon, TrashIcon} from 'shells/icon';
import {SingleInputModal} from 'shells/single-input-modal/single-input-modal';
import {LegacySetupTableWrapper} from 'shells/table/legacy-setup-table-wrapper';

import type {EmailSequenceTemplateDuplicateInput} from 'nutshell-graphql-types';

import {getEmailSequenceTemplateFilters} from '../email-utils/get-email-sequence-template-filters';

import {ArchiveGroupModal} from './archive-group-modal';
import type {AfterArchive} from './archive-group-modal';
import {
    useGetEmailTemplateGroup,
    useArchiveEmailSequenceTemplateGroup,
    useUpdateEmailSequenceTemplateGroup,
} from './graphql/hooks';
import {TemplateTable} from './template-table';
import type {Template} from './types';

type Props = {
    groupId: string;
    groupName: string;
    activeFilters: FilterObject[];
    icon: React.ComponentType<any>;
    templates: Template[];
    onAddClick: () => void;
    onDeleteTemplate: (templateId: string) => Promise<any>;
    isLoading: boolean;
    errorMessage: string | null | undefined;
    canArchive?: boolean;
    canRename?: boolean;
    getTableHeaderRef?: (arg1?: HTMLElement | null | undefined) => void;
    setSequenceTemplateId: (arg1: string) => void;
    setSequenceTemplateName: (arg1: string) => void;
    setShouldShowModal: (arg1: boolean) => void;
    setShouldShowRenameModal: (arg1: boolean) => void;
    setShouldShowChangeGroupModal: (arg1: boolean) => void;
    setTemplateGroupId: (arg1: string) => void;
    refetchGroups: () => Promise<any>;
    networkStatus: number;
    onDuplicateTemplate: (arg1: EmailSequenceTemplateDuplicateInput) => Promise<any>;
    isDuplicating: boolean;
};

export function GroupTable(props: Props) {
    const {creatorIds, onlyArchived, includeShared, search, includeUnsharedFromOthers} =
        getEmailSequenceTemplateFilters(props.activeFilters);

    const {emailSequenceTemplateGroup, isLoading} = useGetEmailTemplateGroup({
        search,
        groupId: props.groupId,
        creatorIds,
        onlyArchived,
        includeShared,
        includeUnsharedFromOthers,
    });

    const [isRenaming, setIsRenaming] = React.useState<boolean>(false);
    const [isArchiving, setIsArchiving] = React.useState<boolean>(false);
    const {updateGroup, isUpdating} = useUpdateEmailSequenceTemplateGroup();
    const {archiveGroup, isArchiving: archivingInProgress} = useArchiveEmailSequenceTemplateGroup();

    const templates =
        emailSequenceTemplateGroup && emailSequenceTemplateGroup.emailSequenceTemplates
            ? emailSequenceTemplateGroup.emailSequenceTemplates
            : props.templates;

    const closeRenamingModal = () => setIsRenaming(false);
    const closeArchivingModal = () => setIsArchiving(false);
    const onArchive = (afterArchive: AfterArchive) =>
        archiveGroup({
            id: props.groupId,
            shouldArchiveTemplates: afterArchive === 'archiveTemplates',
        }).then(() => {
            props.refetchGroups();
        });

    const actionMenuOptions = [
        {
            label: 'Create new template in group',
            icon: <PlusIcon size={18} />,
            handleSelect: props.onAddClick,
        },
    ];

    if (props.canRename) {
        actionMenuOptions.unshift({
            label: 'Rename group',
            icon: <EditIcon size={18} />,
            handleSelect: () => setIsRenaming(true),
        });
    }

    if (props.canArchive) {
        actionMenuOptions.push({
            label: 'Archive group',
            icon: <TrashIcon size={16} />,
            // @ts-expect-error
            variant: 'destructive',
            handleSelect: () => {
                if (templates.length === 0) {
                    onArchive('archiveTemplates');
                } else {
                    setIsArchiving(true);
                }
            },
        });
    }

    return (
        <>
            <LegacySetupTableWrapper
                title={props.groupName}
                count={props.templates.length}
                icon={props.icon}
                getTableHeaderRef={props.getTableHeaderRef}
                actionMenuOptions={actionMenuOptions}
            >
                <TemplateTable
                    templates={templates}
                    isLoading={
                        // Network status check ensures we don't show loading state when refetching after the group is archived
                        props.isLoading && !props.templates.length && props.networkStatus === 1
                    }
                    errorMessage={props.errorMessage}
                    onAddRowClick={props.onAddClick}
                    onDelete={props.onDeleteTemplate}
                    fullFragmentLoading={isLoading}
                    setSequenceTemplateId={props.setSequenceTemplateId}
                    setSequenceTemplateName={props.setSequenceTemplateName}
                    setShouldShowModal={props.setShouldShowModal}
                    setShouldShowRenameModal={props.setShouldShowRenameModal}
                    setShouldShowChangeGroupModal={props.setShouldShowChangeGroupModal}
                    setTemplateGroupId={props.setTemplateGroupId}
                    onDuplicateTemplate={props.onDuplicateTemplate}
                    // @ts-expect-error
                    isDuplicating={props.isDuplicating}
                />
            </LegacySetupTableWrapper>
            {isRenaming ? (
                <SingleInputModal
                    title='Rename group'
                    initialValue={props.groupName}
                    onClose={closeRenamingModal}
                    isSaving={isUpdating}
                    onSave={(newName: string) => {
                        updateGroup({id: props.groupId, name: newName}).then(closeRenamingModal);
                    }}
                />
            ) : undefined}
            {isArchiving ? (
                <ArchiveGroupModal
                    onClose={closeArchivingModal}
                    isSaving={archivingInProgress}
                    onArchive={(afterArchive: AfterArchive) => {
                        onArchive(afterArchive).then(closeArchivingModal);
                    }}
                />
            ) : undefined}
        </>
    );
}
